import { User } from "../models/user.model";
import { joinUrlSegments, removeTrailingSlash } from "../utils/urlUtils";
import { BackendHttpService } from "./backendHttpService";

export class AdministrationService extends BackendHttpService {
  private static userCache: User | null = null;
  private static cacheTimestamp: number = 0;
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

  constructor() {
    super("administration");
  }

  async getCurrentPerson(): Promise<User> {
    try {
      const now = Date.now();

      // Check if we have valid cached data
      if (
        AdministrationService.userCache &&
        now - AdministrationService.cacheTimestamp <
        AdministrationService.CACHE_DURATION
      ) {
        return AdministrationService.userCache;
      }

      // Use the root API endpoint instead of administration
      const accessToken = await this.getAccessToken();

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": localStorage.getItem("language") || "en",
      };

      // Build the CurrentPerson URL using URL utilities
      const baseUrl = removeTrailingSlash(
        this.apiUrl.replace(/\/administration\/?$/, "")
      );
      const currentPersonUrl = joinUrlSegments(baseUrl, "CurrentPerson");

      const response = await fetch(currentPersonUrl, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        // Enhanced error handling for new users
        if (response.status === 404) {
          throw new Error(
            `User not found in system - status: ${response.status}. This may be a new user that hasn't been created in the backend yet.`
          );
        } else if (response.status === 400) {
          throw new Error(
            `Bad request - status: ${response.status}. This may be a new user or data validation issue.`
          );
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const userData = await response.json();

      // Update static cache
      AdministrationService.userCache = userData;
      AdministrationService.cacheTimestamp = now;

      return userData;
    } catch (error) {
      console.error(
        "[AdministrationService] Error getting current person:",
        error
      );

      // Clear cache on error to allow retry
      AdministrationService.userCache = null;
      AdministrationService.cacheTimestamp = 0;

      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    // This method is deprecated in favor of getCurrentPerson which provides more complete user data
    // The old implementation fetched user data from /current endpoint with caching
    // Now we delegate to getCurrentPerson which handles authentication and provides richer user info
    try {
      const now = Date.now();

      // Check if we have valid cached data
      if (
        AdministrationService.userCache &&
        now - AdministrationService.cacheTimestamp <
        AdministrationService.CACHE_DURATION
      ) {
        return AdministrationService.userCache;
      }

      const userData = await this.getCurrentPerson();

      // Update static cache
      AdministrationService.userCache = userData;
      AdministrationService.cacheTimestamp = now;

      return userData;
    } catch (error) {
      // Clear cache on error to allow retry
      AdministrationService.userCache = null;
      AdministrationService.cacheTimestamp = 0;
      throw error;
    }
  }

  async getAdmins(): Promise<User[]> {
    try {
      return await this.get<User[]>("/admins");
    } catch (error) {
      console.error("Error getting admins:", error);
      throw error;
    }
  }

  async getManagers(): Promise<User[]> {
    try {
      return await this.get<User[]>("/managers");
    } catch (error) {
      console.error("Error getting managers:", error);
      throw error;
    }
  }

  async createAdmin(email: string): Promise<User> {
    try {
      return await this.put<{ email: string }, User>({ email }, "/admin");
    } catch (error) {
      console.error("Error creating admin:", error);
      throw error;
    }
  }

  async createManager(email: string): Promise<User> {
    try {
      return await this.put<{ email: string }, User>({ email }, "/manager");
    } catch (error) {
      console.error("Error creating manager:", error);
      throw error;
    }
  }

  async deleteUser(email: string): Promise<boolean> {
    try {
      const accessToken = await this.getAccessToken();

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": localStorage.getItem("language") || "en",
      };

      const response = await fetch(
        this.buildUrl(`users/${encodeURIComponent(email)}`),
        {
          method: "DELETE",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error deleting user with email ${email}:`, error);
      throw error;
    }
  }

  clearCache(): void {
    AdministrationService.userCache = null;
    AdministrationService.cacheTimestamp = 0;
  }

  // Static method to clear cache from anywhere
  static clearCache(): void {
    AdministrationService.userCache = null;
    AdministrationService.cacheTimestamp = 0;
  }
}
